import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
	Button,
	Select,
	PropertyFilter,
	PropertyFilterProps,
	SpaceBetween,
} from '@amzn/awsui-components-console';

import { PropertyFilteri18nStrings } from 'src/constants/ComponentConstants';
import {
	DEFAULT_WORKFLOW_FILTER_SCHEMA,
	getWorkflowFiltersByName,
} from 'src/constants/WorkflowDetails';
import {
	PROPERTY_FILTER_PLACEHOLDER_TEXT,
	SELECT_WORKFLOW_MESSAGES,
} from 'src/pages/entitySearch/entitySearchConstants';
import { REGIONS, AWS_REGIONS } from 'src/constants/CommonConstants';
import { WorkflowStatus } from 'src/constants/commonEnums';
import {
	EntitySearchProps,
	WorkflowOptionType,
	FilterItem,
} from 'src/pages/entitySearch/entitySearch.interface';
import { useAuthContext } from 'src/providers/AuthContext';
import 'src/pages/entitySearch/entitySearch.scss';

export const EntitySearchFilter = ({
	workflowType,
	changeFilterKeyValuePair,
	handleOnChangeForSelect,
	workflowListDropdown,
	selectedOption,
	setSearchButtonDisabled,
	handleOnClickClearSelectionButton,
	loadWorkflowOptionsStatus,
	clearFiltersButtonDisabled,
	setClearFiltersButtonDisabled,
	favoriteWorkflowIds,
	onToggleFavorite,
	initialFilterTokens = [],
	filterTokens,
}: EntitySearchProps) => {
	const { userName } = useAuthContext();
	const { WORKFLOW_TYPE_UNSELECTED, FILTER } = PROPERTY_FILTER_PLACEHOLDER_TEXT;
	const [query, setQuery] = useState<PropertyFilterProps.Query>({
		// URL encodes only `=` (PropertyFilter is rendered with hideOperations).
		// Encoding new operators in the URL is required if hideOperations is removed.
		tokens: initialFilterTokens
			.filter((f: FilterItem) => f.key && f.value)
			.map((f: FilterItem) => ({
				propertyKey: f.key,
				value: f.value,
				operator: '=',
			})),
		operation: 'and',
	});

	// Mirror the authoritative filter list from the parent. This keeps the
	// PropertyFilter chips in lock-step with the parent's filterList — whether
	// the parent cleared bulkExecutionId on a workflow change, or the user just
	// edited tokens directly.
	const lastMirroredRef = useRef<string>('');

	useEffect(() => {
		if (!filterTokens) return;

		const normalized = filterTokens
			.filter((f) => f.key && f.value)
			.map((f) => ({ propertyKey: f.key, value: f.value, operator: '=' }));
		const signature = normalized
			.map((t) => `${t.propertyKey}=${t.value}`)
			.sort()
			.join('|');

		if (signature === lastMirroredRef.current) return;
		lastMirroredRef.current = signature;

		setQuery((prev) => ({ ...prev, tokens: normalized }));
	}, [filterTokens]);

	const isLoading = loadWorkflowOptionsStatus === 'loading';
	const hasWorkflowOptions =
		workflowListDropdown && workflowListDropdown.length > 1;

	const statusFilteringOptions: PropertyFilterProps.FilteringOption[] =
		Object.values(WorkflowStatus).map((status) => ({
			propertyKey: 'status',
			value: status,
		}));

	const regionFilteringOptions: PropertyFilterProps.FilteringOption[] =
		REGIONS.map((region) => ({
			propertyKey: 'region',
			value: region.id,
		}));

	const awsRegionFilteringOptions: PropertyFilterProps.FilteringOption[] =
		AWS_REGIONS.map((awsRegion) => ({
			propertyKey: 'awsRegion',
			value: awsRegion.value,
			label: awsRegion.label,
		}));

	const filteringOptions = [
		...statusFilteringOptions,
		...regionFilteringOptions,
		...awsRegionFilteringOptions,
		...(userName
			? [{ propertyKey: 'createdBy', value: userName, label: `${userName}` }]
			: []),
	];

	const favoriteSet = useMemo(
		() => new Set(favoriteWorkflowIds),
		[favoriteWorkflowIds]
	);

	const workflowOptionsWithFavorites = useMemo(() => {
		return workflowListDropdown.map((option) => {
			const isFav = favoriteSet.has(option.value);

			return {
				...option,
				labelTag: isFav ? '★' : undefined,
			};
		});
	}, [workflowListDropdown, favoriteSet]);

	const selectedWorkflowIsFavorite = selectedOption
		? favoriteSet.has(selectedOption.value)
		: false;

	const setQueryChange = (detail) => {
		const normalizedTokens = detail.tokens.map((t) => {
			if (t.propertyKey) {
				return t;
			}

			const filterText = typeof t.value === 'string' ? t.value.toLowerCase() : '';
			const firstMatchingSuggestion = filterText
				? filteringOptions.find((option) =>
					[option.value, option.label]
						.filter((value): value is string => Boolean(value))
						.some((value) => value.toLowerCase().includes(filterText))
				)
				: undefined;

			return firstMatchingSuggestion
				? {
					...t,
					propertyKey: firstMatchingSuggestion.propertyKey,
					value: firstMatchingSuggestion.value,
					operator: t.operator ?? '=',
				}
				: { ...t, propertyKey: 'entityName', operator: t.operator ?? '=' };
		});
		const tokensMap = new Map(
			normalizedTokens
				.filter((t) => t.propertyKey && t.value)
				.map((t) => [t.propertyKey, t])
		);
		const tokens = Array.from(
			tokensMap.values()
		) as PropertyFilterProps.Token[];
		const filterList: object[] = [];

		tokens.forEach((token) => {
			filterList.push({
				key: token.propertyKey,
				value: token.value,
			});
		});

		changeFilterKeyValuePair(filterList);
		setQuery({
			tokens,
			operation: 'and',
		});
	};

	return (
		<SpaceBetween direction="horizontal" size="xs">
			<SpaceBetween direction="horizontal" size="xxxs">
				<div style={{ minWidth: '200px' }}>
					<Select
						selectedOption={
							workflowType ? (selectedOption as WorkflowOptionType) : null
						}
						onChange={({ detail }) => {
							handleOnChangeForSelect(detail);
						}}
						options={workflowOptionsWithFavorites}
						statusType={loadWorkflowOptionsStatus}
						loadingText={SELECT_WORKFLOW_MESSAGES.LOADING}
						errorText={SELECT_WORKFLOW_MESSAGES.ERROR}
						filteringType="auto"
						placeholder={
							isLoading
								? SELECT_WORKFLOW_MESSAGES.LOADING
								: hasWorkflowOptions
									? SELECT_WORKFLOW_MESSAGES.SELECT
									: SELECT_WORKFLOW_MESSAGES.NO_OPTIONS
						}
						disabled={isLoading}
						ariaLabel={
							isLoading
								? SELECT_WORKFLOW_MESSAGES.LOADING_LABEL
								: SELECT_WORKFLOW_MESSAGES.SELECT_LABEL
						}
						inlineLabelText={SELECT_WORKFLOW_MESSAGES.INLINE_LABEL}
					/>
				</div>
				{selectedOption && (
					<div style={{ paddingTop: '14px' }}>
						<Button
							variant="icon"
							iconName={selectedWorkflowIsFavorite ? 'star-filled' : 'star'}
							ariaLabel={
								selectedWorkflowIsFavorite
									? 'Remove from favorites'
									: 'Add to favorites'
							}
							data-testid="favorite-toggle-button"
							onClick={() => onToggleFavorite(selectedOption.value)}
						/>
					</div>
				)}
			</SpaceBetween>
			<div id="propertyFilter" style={{ paddingTop: '14px' }}>
				<PropertyFilter
					onChange={({ detail }) => {
						setQueryChange(detail);
						setSearchButtonDisabled(false);
						setClearFiltersButtonDisabled(true);
					}}
					hideOperations
					query={query}
					i18nStrings={{
						...PropertyFilteri18nStrings,
						groupPropertiesText: 'Filters',
						clearFiltersText: 'Clear Filters',
						filteringPlaceholder:
							workflowType == '' ? WORKFLOW_TYPE_UNSELECTED : FILTER,
					}}
					tokenLimit={6}
					filteringProperties={
						workflowType == ''
							? DEFAULT_WORKFLOW_FILTER_SCHEMA.filters
							: getWorkflowFiltersByName(workflowType).filters
					}
					filteringOptions={filteringOptions}
					disabled={isLoading}
				/>
			</div>

			{workflowType && (
				<div style={{ paddingTop: '14px' }}>
					<Button
						data-testid="clearFiltersButton"
						onClick={() => {
							setQuery({
								tokens: [],
								operation: 'and',
							});
							handleOnClickClearSelectionButton();
						}}
						disabled={clearFiltersButtonDisabled || isLoading}
					>
						Reset Search Criteria
					</Button>
				</div>
			)}
		</SpaceBetween>
	);
};
